<template>
  <div class="inline-flex justify-center">
    <!-- add a eos-icons:three-dots-loading icon while loading -->
    <!-- Loading state: heavily blurred skeleton with horizontal padding for blur feathering -->
    <div v-if="loading" :class="cn('relative px-4 py-1', props.class)">
      <div class="blur-[0px] select-none opacity-60">{{ placeholderText }}</div>
      <div class="absolute inset-0 bg-gradient-to-r from-transparent via-background to-transparent dark:from-transparent dark:via-muted dark:to-transparent animate-shimmer overflow-hidden"></div>
      <div class="absolute inset-0 flex items-center justify-center">
        <!-- <Icon name="eos-icons:three-dots-loading" class="w-12 h-12 text-muted-foreground" /> -->
      </div>
    </div>
    
    <!-- Loaded state: pullup animation -->
    <div v-else class="inline-flex">
      <Motion
        v-for="(char, index) in characters"
        :key="`${char}-${index}-${animationKey}`"
        as="span"
        :initial="pullupVariant.initial"
        :animate="pullupVariant.animate"
        :transition="{
          delay: index * (props.delay || 0.03),
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }"
        :class="cn('inline-block', char === ' ' ? 'w-2' : '', props.class)"
      >
        <span v-if="char === ' '">&nbsp;</span>
        <span v-else>{{ char }}</span>
      </Motion>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, type HTMLAttributes } from "vue";
import { Motion } from "motion-v";
import { cn } from "@/lib/utils";

interface AnimatedNumberProps {
  class?: HTMLAttributes["class"];
  value: number;
  loading?: boolean;
  delay?: number;
  formatOptions?: Intl.NumberFormatOptions;
  lastKnownValue?: number;
}

const props = withDefaults(defineProps<AnimatedNumberProps>(), {
  delay: 0.03,
  formatOptions: () => ({
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
});

const animationKey = ref(0);

// Format the number
const formattedNumber = computed(() => {
  return new Intl.NumberFormat('en-US', props.formatOptions).format(props.value);
});

// Split into characters for animation
const characters = computed(() => {
  return formattedNumber.value.split("");
});

// Generate placeholder text for loading - use last known value or fallback to random
const placeholderText = computed(() => {
  if (props.lastKnownValue !== undefined) {
    // Use the last known value formatted with the same options
    return new Intl.NumberFormat('en-US', props.formatOptions).format(props.lastKnownValue);
  }
  
  // Fallback to random numbers if no last known value
  if (props.formatOptions?.style === 'currency' || formattedNumber.value.includes('$')) {
    const randomAmount = (Math.random() * 50 + 10).toFixed(2);
    return `$${randomAmount}`;
  } else if (formattedNumber.value.includes('.')) {
    const randomDecimal = (Math.random() * 1000 + 100).toFixed(2);
    return randomDecimal;
  } else {
    const randomInt = Math.floor(Math.random() * 9000 + 1000);
    return randomInt.toString();
  }
});

const pullupVariant = {
  initial: { y: 30, opacity: 0 },
  animate: {
    y: 0,
    opacity: 1,
  },
};

// Watch for value changes to trigger re-animation
watch(
  () => props.value,
  () => {
    if (!props.loading) {
      // Increment key to force re-animation
      animationKey.value++;
    }
  }
);

// Watch for loading state changes
watch(
  () => props.loading,
  (newLoading, oldLoading) => {
    if (oldLoading && !newLoading) {
      // Just finished loading, trigger animation
      animationKey.value++;
    }
  }
);
</script>

<style scoped>
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 1.5s infinite;
}
</style> 